import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import type { DateRange } from 'react-day-picker';

import { Calendar } from '@/_shared/components/ui/calendar';

import { CalendarPopover } from './CalendarPopover';
import { ObservedRange } from './ObservedRange';

/** The chip label for a `[start, end]` tuple — dates in the foreground with a muted "and" separator, and a
 *  muted `--` standing in for whichever end isn't picked yet. Drops the start year when it matches the end. */
function formatRange(start: string | null, end: string | null): ReactNode {
  if (!start && !end) return 'Pick a range';
  const from = start ? dayjs(start) : null;
  const to = end ? dayjs(end) : null;
  const and = <span className="font-normal text-muted-foreground">and</span>;
  const missing = <span className="font-normal text-muted-foreground">--</span>;
  if (from && to) {
    const startFormat = from.year() === to.year() ? 'MMM D' : 'MMM D, YYYY';
    return (
      <>
        {from.format(startFormat)} {and} {to.format('MMM D, YYYY')}
      </>
    );
  }
  if (from)
    return (
      <>
        {from.format('MMM D, YYYY')} {and} {missing}
      </>
    );
  return (
    <>
      {missing} {and} {to!.format('MMM D, YYYY')}
    </>
  );
}

/** A start–end date range fused into an InputGroup — one chip shows the formatted span (dayjs) and opens
 *  a Popover calendar in range mode. Value is a `[start, end]` tuple of `YYYY-MM-DD` strings (either may
 *  be null while the range is half-picked). Closes once both ends are set.
 *
 *  `min`/`max` are the column's OBSERVED range — see {@link DateValue} for why they're advisory rather
 *  than a picking constraint. */
export function DateRangeValue({
  value,
  onChange,
  ariaLabel,
  min,
  max,
}: {
  value: [string | null, string | null] | null | undefined;
  onChange: (range: [string | null, string | null]) => void;
  ariaLabel: string;
  min?: string;
  max?: string;
}) {
  const [start, end] = value ?? [null, null];
  const selected: DateRange | undefined = start
    ? { from: dayjs(start).toDate(), to: end ? dayjs(end).toDate() : undefined }
    : undefined;
  // Open on the selection, else on the observed range's end — never a blank month a thousand rows away
  // from the data.
  const defaultMonth = selected?.from ?? (max ? dayjs(max).toDate() : undefined);

  return (
    <CalendarPopover
      ariaLabel={ariaLabel}
      hasValue={start != null || end != null}
      label={formatRange(start, end)}
      triggerVariant="text"
      triggerClassName="px-2"
    >
      {(close) => (
        <div className="flex flex-col gap-2">
          <Calendar
            mode="range"
            numberOfMonths={2}
            selected={selected}
            {...(defaultMonth ? { defaultMonth } : {})}
            // Driven from our own value (not react-day-picker's accumulated range): the first click sets one
            // end and keeps the popover open; the second completes the range (ordered) and closes it.
            onSelect={(_range, clickedDay) => {
              const clicked = dayjs(clickedDay).format('YYYY-MM-DD');
              if (start && !end) {
                onChange(start <= clicked ? [start, clicked] : [clicked, start]);
                close();
              } else if (!start && end) {
                onChange(end <= clicked ? [end, clicked] : [clicked, end]);
                close();
              } else {
                onChange([clicked, null]);
              }
            }}
            autoFocus
            showOutsideDays={false}
            captionLayout="dropdown"
          />
          <ObservedRange min={min} max={max} />
        </div>
      )}
    </CalendarPopover>
  );
}
